import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Wallet, Plus, Minus, TrendingUp, TrendingDown,
  DollarSign, Receipt
} from 'lucide-react'
import { deliveryWalletApi } from '../../lib/api'
import toast from 'react-hot-toast'

interface WalletData {
  id: string
  delivery_id: string
  store_id: string
  current_balance: number
  store_name: string
  delivery_name: string
}

interface Transaction {
  id: string
  transaction_type: 'money_received' | 'sale_payment' | 'money_delivered' | 'adjustment'
  amount: number
  order_id?: string
  description: string
  created_at: string
  customer_name?: string
  order_total?: number
  breakdown?: Array<{
    denomination: number
    name: string
    type: 'bill' | 'coin'
    quantity: number
    total_amount: number
  }>
}

const DeliveryWallet = () => {
  const navigate = useNavigate()
  const [wallet, setWallet] = useState<WalletData | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [showDeliverModal, setShowDeliverModal] = useState(false)
  const [deliverAmount, setDeliverAmount] = useState('')
  const [pendingTransfers, setPendingTransfers] = useState<any[]>([])
  const [loadingTransfers, setLoadingTransfers] = useState(false)

  useEffect(() => {
    loadWallet()
    loadPendingTransfers()
  }, [])

  const loadWallet = async () => {
    try {
      setLoading(true)
      const response = await deliveryWalletApi.getWallet()
      if (response.success) {
        // Asegurar que current_balance sea un número
        const walletData = response.data.wallet
        if (walletData) {
          walletData.current_balance = Number(walletData.current_balance || 0)
        }
        setWallet(walletData)
        
        // Asegurar que los amounts de las transacciones sean números
        const transactionsData = (response.data.transactions || []).map((t: any) => ({
          ...t,
          amount: Number(t.amount || 0)
        }))
        setTransactions(transactionsData)
      }
    } catch (error: any) {
      console.error('Error loading wallet:', error)
      toast.error('Error al cargar cartera')
    } finally {
      setLoading(false)
    }
  }

  const loadPendingTransfers = async () => {
    try {
      setLoadingTransfers(true)
      const response = await deliveryWalletApi.getPendingTransfers()
      if (response.success) {
        setPendingTransfers(response.data || [])
      }
    } catch (error: any) {
      console.error('Error loading pending transfers:', error)
    } finally {
      setLoadingTransfers(false)
    }
  }

  const handleConfirmTransfer = async (transferId: string) => {
    try {
      const response = await deliveryWalletApi.confirmTransfer(transferId)
      if (response.success) {
        toast.success('Transferencia confirmada exitosamente')
        loadWallet()
        loadPendingTransfers()
      }
    } catch (error: any) {
      console.error('Error confirming transfer:', error)
      toast.error(error.response?.data?.message || 'Error al confirmar transferencia')
    }
  }

  const handleDeliverMoney = async () => {
    if (!deliverAmount || parseFloat(deliverAmount) <= 0) {
      toast.error('Ingresa un monto válido')
      return
    }

    if (!wallet) {
      toast.error('No se pudo obtener la información de la cartera')
      return
    }

    const currentBalance = Number(wallet.current_balance || 0)
    if (parseFloat(deliverAmount) > currentBalance) {
      toast.error(`Balance insuficiente. Balance actual: RD$${currentBalance.toFixed(2)}`)
      return
    }

    try {
      const response = await deliveryWalletApi.deliverMoney({
        amount: parseFloat(deliverAmount)
      })
      if (response.success) {
        toast.success('Dinero entregado exitosamente')
        setDeliverAmount('')
        setShowDeliverModal(false)
        loadWallet()
      }
    } catch (error: any) {
      console.error('Error delivering money:', error)
      toast.error(error.response?.data?.message || 'Error al entregar dinero')
    }
  }

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'money_received':
        return <TrendingUp className="w-5 h-5 text-green-600" />
      case 'sale_payment':
        return <DollarSign className="w-5 h-5 text-blue-600" />
      case 'money_delivered':
        return <TrendingDown className="w-5 h-5 text-red-600" />
      default:
        return <Receipt className="w-5 h-5 text-gray-600" />
    }
  }

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'money_received':
        return 'bg-green-50 border-green-200'
      case 'sale_payment':
        return 'bg-blue-50 border-blue-200'
      case 'money_delivered':
        return 'bg-red-50 border-red-200'
      default:
        return 'bg-gray-50 border-gray-200'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('es-DO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="flex items-center justify-center h-screen">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-blue-600"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 pb-24">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-2xl border-b border-white/60 sticky top-0 z-10">
        <div className="max-w-md lg:max-w-7xl mx-auto px-4 lg:px-8 py-5 lg:py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/store/delivery')}
              className="p-3 lg:p-4 -ml-2 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all active:scale-95 shadow-lg shadow-black/5"
            >
              <ArrowLeft className="w-6 h-6 lg:w-7 lg:h-7 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Mi Cartera
              </h1>
              {wallet && (
                <p className="text-xs lg:text-sm text-gray-500 font-medium mt-0.5">
                  {wallet.store_name}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-md lg:max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
        {/* Balance Card */}
        {wallet && (
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-blue-500/30 p-6 lg:p-8 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Wallet className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                </div>
                <div>
                  <p className="text-white/80 text-sm lg:text-base font-medium">Balance Actual</p>
                  <p className="text-white text-2xl lg:text-4xl font-bold">
                    RD${Number(wallet.current_balance || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Transfers */}
        {pendingTransfers.length > 0 && (
          <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8 mb-6">
            <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-4 lg:mb-6">
              Transferencias Pendientes
            </h2>
            <div className="space-y-4">
              {pendingTransfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-bold text-gray-900 text-lg">
                        RD${Number(transfer.amount || 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {transfer.description || 'Dinero para devueltas'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        De: {transfer.created_by_name} • {formatDate(transfer.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleConfirmTransfer(transfer.id)}
                      className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-green-500/30 active:scale-95 transition-transform"
                    >
                      Confirmar Recepción
                    </button>
                  </div>
                  {transfer.breakdown && transfer.breakdown.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Desglose:</p>
                      <div className="flex flex-wrap gap-2">
                        {transfer.breakdown.map((item: any, idx: number) => (
                          <span
                            key={idx}
                            className="text-xs bg-white px-2 py-1 rounded-lg border border-amber-200"
                          >
                            {item.quantity}x {item.name} = RD${Number(item.total_amount || 0).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6">
          <button
            onClick={() => setShowDeliverModal(true)}
            className="w-full bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-2xl p-4 lg:p-6 font-bold text-base lg:text-lg shadow-lg shadow-red-500/30 active:scale-95 transition-transform flex items-center justify-center gap-2"
          >
            <Minus className="w-5 h-5 lg:w-6 lg:h-6" />
            Entregar Dinero al Negocio
          </button>
        </div>

        {/* Transactions */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-4 lg:mb-6">
            Historial de Transacciones
          </h2>
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No hay transacciones aún</p>
            </div>
          ) : (
            <div className="space-y-4">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className={`rounded-2xl p-4 lg:p-6 border-2 ${getTransactionColor(transaction.transaction_type)}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getTransactionIcon(transaction.transaction_type)}
                      <div>
                        <p className="font-bold text-gray-900">
                          {transaction.transaction_type === 'money_received' && 'Dinero Recibido'}
                          {transaction.transaction_type === 'sale_payment' && 'Pago de Venta'}
                          {transaction.transaction_type === 'money_delivered' && 'Dinero Entregado'}
                          {transaction.transaction_type === 'adjustment' && 'Ajuste'}
                        </p>
                        <p className="text-sm text-gray-600">{transaction.description}</p>
                        {transaction.customer_name && (
                          <p className="text-xs text-gray-500 mt-1">
                            Cliente: {transaction.customer_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${
                        transaction.transaction_type === 'money_delivered' ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {transaction.transaction_type === 'money_delivered' ? '-' : '+'}
                        RD${transaction.amount.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(transaction.created_at)}
                      </p>
                    </div>
                  </div>
                  {transaction.breakdown && transaction.breakdown.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Desglose:</p>
                      <div className="flex flex-wrap gap-2">
                        {transaction.breakdown.map((item, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-white px-2 py-1 rounded-lg border border-gray-200"
                          >
                            {item.quantity}x {item.name} = RD${item.total_amount.toFixed(2)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Deliver Money Modal */}
      {showDeliverModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 lg:p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">
              Entregar Dinero al Negocio
            </h3>
            {wallet && (
              <p className="text-sm text-gray-600 mb-4">
                Balance disponible: RD${Number(wallet.current_balance || 0).toFixed(2)}
              </p>
            )}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Monto (RD$)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={wallet ? Number(wallet.current_balance || 0) : 0}
                value={deliverAmount}
                onChange={(e) => setDeliverAmount(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl text-lg font-bold focus:border-blue-500 focus:outline-none"
                placeholder="0.00"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeliverModal(false)
                  setDeliverAmount('')
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-2xl font-bold active:scale-95 transition-transform"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeliverMoney}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-2xl font-bold active:scale-95 transition-transform"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeliveryWallet

